import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ProductContext } from '../ProductProvider';
import { Helmet } from 'react-helmet-async';
import lib from '../assets/images/library/library.png';
import lib2 from '../assets/images/library/lib2.png';
import lib3 from '../assets/images/library/lib3.png';
import './AboutUsMain.css';

export default function AboutUsMain() {
  const { loggedUser, validateLogin } = useContext(ProductContext);
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    if (!loggedUser.isAuthenticated) {
      validateLogin();
    }
  }, [loggedUser]);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 250);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="aboutus-page-container">
      <Helmet>
        <title>About the Library | Vidyajyoti College of Theology - Delhi</title>
        <meta
          name="description"
          content="Learn about the Flynn - Meagher Library at Vidyajyoti College of Theology, holding over 1,34,000 books and 228 journals in sacred theology and religious studies."
        />
        <meta
          name="keywords"
          content="Vidyajyoti Library, Theological Research, Delhi University Theology, JNU Research, VJTR Journal, Christian Theology Library, Religious Studies"
        />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
      </Helmet>

      <div className="aboutus-wrapper">
        {/* Navigation Breadcrumbs */}
        <div className="aboutus-breadcrumb">
          <Link to="/">
            <i className="fa fa-home"></i> Home
          </Link>
          <span>/</span>
          <span style={{ color: '#0f172a', fontWeight: 600 }}>About the Library</span>
        </div>

        {/* Hero Heritage Banner */}
        <div className="aboutus-hero-card">
          <div className="aboutus-hero-badge">
            <i className="fa fa-university"></i>
            <span>Pontifical Faculty of Theology • Established 1879</span>
          </div>

          <h1 className="aboutus-hero-title">
            The Flynn - Meagher Library & Research Repository
          </h1>

          <p className="aboutus-hero-sub">
            A venerable sanctuary of theological wisdom, biblical hermeneutics, and religious scholarship serving scholars, researchers, and academic institutions worldwide.
          </p>

          <div className="aboutus-hero-stats-grid">
            <div className="aboutus-hero-stat-box">
              <div className="aboutus-hero-stat-num">1,34,000+</div>
              <div className="aboutus-hero-stat-label">Physical Volumes</div>
            </div>

            <div className="aboutus-hero-stat-box">
              <div className="aboutus-hero-stat-num">228+</div>
              <div className="aboutus-hero-stat-label">Active Journals</div>
            </div>

            <div className="aboutus-hero-stat-box">
              <div className="aboutus-hero-stat-num">65</div>
              <div className="aboutus-hero-stat-label">Academic Disciplines</div>
            </div>

            <div className="aboutus-hero-stat-box">
              <div className="aboutus-hero-stat-num">140+ Yrs</div>
              <div className="aboutus-hero-stat-label">Scholarly Legacy</div>
            </div>
          </div>
        </div>

        {/* Section 1: Institutional Heritage & Mission */}
        <div className="aboutus-card">
          <div className="aboutus-card-title">
            <div className="aboutus-card-title-icon">
              <i className="fa fa-landmark"></i>
            </div>
            <span>Institutional Heritage & Scholarly Mission</span>
          </div>

          <p className="aboutus-narrative-p">
            Vidyajyoti College of Theology (originally established in 1879 in Asansol, later moved to Kurseong, and situated in Delhi since 1972) is an ecclesiastical faculty of theology entrusted with the intellectual and pastoral formation of scholars and researchers across Asia and the world.
          </p>

          <p className="aboutus-narrative-p">
            The <strong>Flynn - Meagher Library</strong> stands as one of the most comprehensive theological repositories in South Asia. Holding approximately <strong>1,34,000 books</strong> and <strong>228 scholarly journals</strong>, the library actively supports rigorous research in Biblical Studies, Systematic & Dogmatic Theology, Moral Theology, Church History, Liturgy, Missiology, Christian Spirituality, Philosophy, Indology, and Comparative Religions.
          </p>

          <p className="aboutus-narrative-p">
            Beyond the faculty and students of Vidyajyoti, the library serves as a vital research hub for professors, doctoral scholars, and postgraduate students from premier national and international universities, including <strong>Delhi University (DU)</strong>, <strong>Jawaharlal Nehru University (JNU)</strong>, <strong>Jamia Millia Islamia</strong>, as well as visiting ecclesiastical researchers from across the globe.
          </p>

          {/* 4 Core Collection Pillars */}
          <div className="collection-pillars-grid">
            <div className="collection-pillar-item">
              <div className="collection-pillar-icon">
                <i className="fa fa-book-open"></i>
              </div>
              <div className="collection-pillar-title">Biblical & Theological Holdings</div>
              <div className="collection-pillar-desc">
                Exhaustive collections in Old & New Testament exegesis, Patristic literature, Dogmatic formulations, and Asian Contextual Theology.
              </div>
            </div>

            <div className="collection-pillar-item">
              <div className="collection-pillar-icon">
                <i className="fa fa-newspaper"></i>
              </div>
              <div className="collection-pillar-title">VJTR Journal Archives</div>
              <div className="collection-pillar-desc">
                Complete serial archives and bound volumes of the renowned <em>Vidyajyoti Journal of Theological Reflection (VJTR)</em> and global ecclesiastical publications.
              </div>
            </div>

            <div className="collection-pillar-item">
              <div className="collection-pillar-icon">
                <i className="fa fa-archive"></i>
              </div>
              <div className="collection-pillar-title">Special & Rare Collections</div>
              <div className="collection-pillar-desc">
                Historical manuscripts, rare Indological treatises, Pontifical council documents, and doctoral research dissertations.
              </div>
            </div>

            <div className="collection-pillar-item">
              <div className="collection-pillar-icon">
                <i className="fa fa-barcode"></i>
              </div>
              <div className="collection-pillar-title">Digital Barcode OPAC</div>
              <div className="collection-pillar-desc">
                Fully computerised discovery system with barcode checkout, Dewey classification indexing, and multi-parameter search.
              </div>
            </div>
          </div>
        </div>

        {/* Section 2: External Scholars Policy & Access Rules */}
        <div className="aboutus-card">
          <div className="aboutus-card-title">
            <div className="aboutus-card-title-icon">
              <i className="fa fa-user-graduate"></i>
            </div>
            <span>External Research Scholars & Guest Usage Policy</span>
          </div>

          <p className="aboutus-narrative-p">
            In keeping with our academic mission to foster ecumenical dialogue and scholarly research, the Vidyajyoti Library welcomes registered researchers from recognized universities and institutions subject to the following regulations:
          </p>

          <div className="scholar-rules-box">
            <div className="scholar-rules-header">
              <i className="fa fa-clipboard-check"></i>
              <span>Terms and Conditions for External Students & Visiting Scholars</span>
            </div>

            <ul className="scholar-rules-list">
              <li className="scholar-rules-item">
                <i className="fa fa-check-circle scholar-rules-item-icon"></i>
                <div>
                  <strong>Official Recommendation:</strong> A formal letter from your research guide or department head (on the university's official letterhead) requesting library access must be submitted.
                </div>
              </li>

              <li className="scholar-rules-item">
                <i className="fa fa-check-circle scholar-rules-item-icon"></i>
                <div>
                  <strong>Identity Documentation:</strong> A photocopy of your valid University Identity Card, Aadhar Card, address proof, and contact details must be deposited at the circulation desk.
                </div>
              </li>

              <li className="scholar-rules-item">
                <i className="fa fa-check-circle scholar-rules-item-icon"></i>
                <div>
                  <strong>Academic Declaration:</strong> A personal declaration stating (a) the academic research topic, (b) assurance that library materials are used strictly for academic purposes, and (c) an undertaking to provide bibliographic acknowledgment of Vidyajyoti Library in published papers/theses.
                </div>
              </li>

              <li className="scholar-rules-item">
                <i className="fa fa-check-circle scholar-rules-item-icon"></i>
                <div>
                  <strong>On-Site Reference Access Only:</strong> External scholars are permitted to consult books and journals within the air-conditioned reference reading room. Taking library books outside the premises is strictly prohibited.
                </div>
              </li>

              <li className="scholar-rules-item">
                <i className="fa fa-check-circle scholar-rules-item-icon"></i>
                <div>
                  <strong>Daily Return & Care:</strong> Reference books are issued on a daily basis and must be returned to the counter prior to closing time. In the event of damage or loss, replacement costs apply.
                </div>
              </li>

              <li className="scholar-rules-item">
                <i className="fa fa-check-circle scholar-rules-item-icon"></i>
                <div>
                  <strong>Archival Materials:</strong> Direct access to rare historical archives and restricted manuscripts requires prior written approval from the Chief Librarian.
                </div>
              </li>

              <li className="scholar-rules-item">
                <i className="fa fa-check-circle scholar-rules-item-icon"></i>
                <div>
                  <strong>Nominal Membership Fee:</strong> External scholars are charged a subsidised fee:
                  <div className="mt-1">
                    <span className="scholar-fee-pill me-2">Monthly Pass: ₹100/-</span>
                    <span className="scholar-fee-pill">Annual Pass: ₹500/-</span>
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Section 3: Library Facilities Photo Gallery */}
        <div className="aboutus-card">
          <div className="aboutus-card-title">
            <div className="aboutus-card-title-icon">
              <i className="fa fa-images"></i>
            </div>
            <span>Library Facilities & Research Environment</span>
          </div>

          <p className="aboutus-narrative-p">
            The library provides expansive reading halls, specialized stacks, bound periodical wings, and quiet individual study carrels designed for intensive contemplation and academic research.
          </p>

          <div className="aboutus-gallery-grid">
            <div className="aboutus-gallery-card">
              <img src={lib3} alt="Main Research Reading Room" className="aboutus-gallery-img" />
              <div className="aboutus-gallery-caption">
                <i className="fa fa-book-reader me-1 text-primary"></i> Main Research Reading Hall
              </div>
            </div>

            <div className="aboutus-gallery-card">
              <img src={lib2} alt="Periodical Stacks & Theological Journals" className="aboutus-gallery-img" />
              <div className="aboutus-gallery-caption">
                <i className="fa fa-newspaper me-1 text-primary"></i> Periodical Stacks & Journal Archives
              </div>
            </div>

            <div className="aboutus-gallery-card">
              <img src={lib} alt="Library Stacks & Cataloguing Wings" className="aboutus-gallery-img" />
              <div className="aboutus-gallery-caption">
                <i className="fa fa-layer-group me-1 text-primary"></i> Theological Classification Wings
              </div>
            </div>
          </div>
        </div>

        {/* Section 4: Administration Team & Contact Desk */}
        <div className="aboutus-card">
          <div className="aboutus-card-title">
            <div className="aboutus-card-title-icon">
              <i className="fa fa-address-book"></i>
            </div>
            <span>Library Administration & Contact Desk</span>
          </div>

          <div className="staff-contact-grid">
            <div className="staff-contact-card">
              <div className="staff-avatar-icon">
                <i className="fa fa-user-tie"></i>
              </div>
              <div>
                <div className="staff-contact-name">Sudeep Panna</div>
                <div className="staff-contact-role">Technical & Cataloguing Services</div>
                <a href="tel:+919205010207" className="staff-contact-link">
                  <i className="fa fa-phone-alt"></i> +91 92050 10207
                </a>
              </div>
            </div>

            <div className="staff-contact-card">
              <div className="staff-avatar-icon">
                <i className="fa fa-user-check"></i>
              </div>
              <div>
                <div className="staff-contact-name">Rohit Varghese</div>
                <div className="staff-contact-role">Circulation & Systems Desk</div>
                <a href="tel:+919899626793" className="staff-contact-link">
                  <i className="fa fa-phone-alt"></i> +91 98996 26793
                </a>
              </div>
            </div>

            <div className="staff-contact-card">
              <div className="staff-avatar-icon">
                <i className="fa fa-envelope-open-text"></i>
              </div>
              <div>
                <div className="staff-contact-name">Chief Librarian Desk</div>
                <div className="staff-contact-role">Official Correspondence</div>
                <a href="mailto:vidyajyotilibrary@gmail.com" className="staff-contact-link">
                  <i className="fa fa-envelope"></i> vidyajyotilibrary@gmail.com
                </a>
              </div>
            </div>
          </div>

          <div className="mt-4 pt-3 border-top d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div style={{ fontSize: '0.92rem', color: '#64748b' }}>
              <i className="fa fa-map-marker-alt me-1 text-danger"></i>
              <strong>Address:</strong> 4A, Raj Niwas Marg, Ludlow Castle, Civil Lines, Delhi - 110054
            </div>
            <div className="d-flex gap-2">
              <Link to="/search" className="btn btn-outline-primary btn-sm" style={{ fontWeight: 600 }}>
                <i className="fa fa-search me-1"></i> Search Catalogue
              </Link>
              <Link to="/contactus" className="btn btn-primary btn-sm" style={{ fontWeight: 600, background: '#063547', borderColor: '#063547' }}>
                <i className="fa fa-paper-plane me-1"></i> Send Inquiry
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Floating Scroll To Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="btn-aboutus-scroll-top"
          aria-label="Scroll to top"
          title="Back to Top"
        >
          <i className="fa fa-arrow-up"></i>
        </button>
      )}
    </div>
  );
}
